import React from "react";
import { useTranslation } from "react-i18next";
import { MapPin, ChevronRight, Weight, Route, Clock, RotateCw } from "lucide-react";
import { Work } from "../../../types/works";
import { WorkStatusBadge, WorkTypeBadge } from "../badges";
import { formatHours, getObjectName, getVehicleLabel } from "../utils";

interface DriverWorkCardProps {
  work: Work;
  onOpen: (work: Work) => void;
}

const Metric: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-1.5 text-xs text-[#5A6460]">
    <span className="text-[#8A9590]">{icon}</span>
    <span className="text-[#8A9590]">{label}</span>
    <span className="font-semibold text-[#2B2F2E]">{value}</span>
  </div>
);

/** Mobile-first card for the driver's assigned-works list (mirrors the shipments ShipmentCard). */
export const DriverWorkCard: React.FC<DriverWorkCardProps> = ({ work, onOpen }) => {
  const { t } = useTranslation("works");
  const hasRoute = !!work.secondaryObject;

  const num = (v?: number) => (v === undefined ? "—" : Number.isInteger(v) ? `${v}` : v.toFixed(1));

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 sm:p-5 shadow-sm flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpen(work)}
          className="text-base font-semibold text-[#0B0F0E] hover:text-[#C97A3C] transition-colors"
        >
          {work.workNumber}
        </button>
        <WorkStatusBadge status={work.status} />
      </div>

      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        <WorkTypeBadge type={work.type} />
        {work.material && (
          <span className="text-xs text-[#8A9590]">{work.material}</span>
        )}
      </div>

      {/* Route */}
      <div className="mt-3">
        {hasRoute ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center gap-1 mb-0.5">
                <MapPin className="h-3.5 w-3.5 text-[#3F9461] shrink-0" />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#8A9590]">
                  {t("driver.card.from")}
                </span>
              </div>
              <p className="text-sm text-[#2B2F2E] truncate">{getObjectName(work.secondaryObject)}</p>
            </div>
            <div>
              <div className="flex items-center gap-1 mb-0.5">
                <MapPin className="h-3.5 w-3.5 text-[#E24B4A] shrink-0" />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#8A9590]">
                  {t("driver.card.to")}
                </span>
              </div>
              <p className="text-sm text-[#2B2F2E] truncate">{getObjectName(work.primaryObject)}</p>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-1 mb-0.5">
              <MapPin className="h-3.5 w-3.5 text-[#C97A3C] shrink-0" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#8A9590]">
                {t("driver.card.object")}
              </span>
            </div>
            <p className="text-sm text-[#2B2F2E] truncate">{getObjectName(work.primaryObject)}</p>
          </div>
        )}
      </div>

      {/* Headline metrics */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {work.type === "cyclic" && (
          <Metric
            icon={<RotateCw className="h-3.5 w-3.5" />}
            label={t("driver.card.rounds")}
            value={
              work.roundsPlanned
                ? `${work.roundsActual ?? 0}/${work.roundsPlanned}`
                : `${work.roundsActual ?? 0}`
            }
          />
        )}
        {(work.tonnage.planned !== undefined || work.tonnage.actual !== undefined) && (
          <Metric
            icon={<Weight className="h-3.5 w-3.5" />}
            label={t("metrics.tonnageShort")}
            value={`${num(work.tonnage.actual)}t`}
          />
        )}
        {(work.distanceKm.planned !== undefined || work.distanceKm.actual !== undefined) && (
          <Metric
            icon={<Route className="h-3.5 w-3.5" />}
            label={t("metrics.distanceShort")}
            value={`${num(work.distanceKm.actual)} km`}
          />
        )}
        {(work.hours.planned !== undefined || work.hours.actual !== undefined) && (
          <Metric
            icon={<Clock className="h-3.5 w-3.5" />}
            label={t("metrics.hoursShort")}
            value={formatHours(work.hours.actual ?? work.hours.planned)}
          />
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-[#F0EFEB] flex items-center justify-between">
        <span className="text-xs text-[#8A9590]">{getVehicleLabel(work)}</span>
        <button
          type="button"
          onClick={() => onOpen(work)}
          className="inline-flex items-center gap-1 px-4 py-2 bg-[#C97A3C] hover:bg-[#B86B30] text-white text-sm font-medium rounded-lg transition-colors"
        >
          {t("driver.card.open")} <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
